/**
 * Operator (superAdmin) gate for Saoirse Console routes.
 *
 * `requireSuperAdmin()` = `requireSession()` + "is this user a superAdmin?".
 * Non-operators get a `NotSuperAdminError`; callers render a friendly notice
 * and send them to `#/edit` (design: SAOIRSE_CONSOLE_DESIGN_2026-06-10 §1).
 *
 * It calls the zero-arg `is_super_admin()` RPC, which the WP1 migration added
 * (live since 2026-09-05) precisely to unblock this guard. The two older
 * overloads — `(uid text)` and `(uid uuid)` — share one argument name, so
 * PostgREST cannot pick a candidate for a client call and answers PGRST203; the
 * zero-arg overload is unambiguous and takes its identity from the JWT, which is
 * also the only identity worth trusting here.
 *
 * (This file previously selected `user_profiles` directly, with a comment
 * asserting "No zero-arg overload exists." That was true when written and false
 * from the moment the migration landed — REVIEW N3.)
 *
 * SECURITY NOTE: this guard is a UX gate only — it decides what the browser
 * renders, never what the database permits. Phase 1 is read-only through
 * already-public RPCs; Phase 2 writes are authorized server-side by RLS
 * (curate doctrine: never trust client identity).
 */
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { requireSession } from './auth-guard';

/** Thrown when a signed-in user is not the operator. */
export class NotSuperAdminError extends Error {
  constructor() {
    super('NOT_SUPER_ADMIN');
    this.name = 'NotSuperAdminError';
  }
}

/**
 * Resolve the current session and verify the user is a superAdmin.
 *
 * - Signed out → `requireSession()` redirects to `#/signin` and throws.
 * - Signed in, not operator → throws `NotSuperAdminError`.
 * - Lookup error → fails closed (treated as not operator, error logged).
 */
export async function requireSuperAdmin(): Promise<Session> {
  const session = await requireSession();
  const { data, error } = await supabase.rpc('is_super_admin');
  if (error) {
    console.error('[operator-guard] is_super_admin failed:', error.message);
    throw new NotSuperAdminError();   // fail closed
  }
  if (data !== true) throw new NotSuperAdminError();
  return session;
}
